
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import StepCard from "../StepCard";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";
import { CALIBRATION_KEYS, CalibrationMetadata } from "@/constants/calibrationKeys";

interface PressureAdvanceResultsViewProps {
  pressureAdvanceStart: string;
  pressureAdvanceStep: string;
  measuredHeight: string;
  tunedPressureAdvance: string;
  onPrevious: () => void;
  onStartOver: () => void;
}

const PressureAdvanceResultsView = ({
  pressureAdvanceStart,
  pressureAdvanceStep,
  measuredHeight,
  tunedPressureAdvance,
  onPrevious,
  onStartOver
}: PressureAdvanceResultsViewProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatWithOriginalSeparator = (value: string, originalInput: string) => {
    if (!value) return value;
    
    let usedSeparator = '.';
    if (originalInput.includes(',')) usedSeparator = ',';
    else if (originalInput.includes("'")) usedSeparator = "'";
    else if (originalInput.includes('٫')) usedSeparator = '٫';
    
    if (usedSeparator !== '.') {
      return value.replace('.', usedSeparator);
    }
    
    return value;
  };

  const getDisplayResult = () => {
    if (!tunedPressureAdvance) return "";
    const referenceInput = pressureAdvanceStart || pressureAdvanceStep || measuredHeight;
    return formatWithOriginalSeparator(tunedPressureAdvance, referenceInput);
  };

  const handleDone = () => {
    // Mark calibration as completed
    const calibrationData: CalibrationMetadata = {
      completed: true,
      completedAt: new Date().toISOString(),
      slicerUsed: "tutorial-mode"
    };
    localStorage.setItem(CALIBRATION_KEYS.pressureAdvance, JSON.stringify(calibrationData));
    
    toast({
      title: "Calibration Complete!",
      description: "Pressure advance calibration marked as complete.",
      duration: 3000
    });
    
    // Navigate back to tools page
    navigate('/tools');
  };

  return (
    <StepCard 
      title="Calibration Complete!"
      onPrevious={onPrevious}
      showPrevious
      customRightButton={
        <Button onClick={handleDone} variant="outline">
          Done
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      }
    >
      <div className="space-y-6">
        <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="text-green-800 dark:text-green-200">
              Your Tuned Pressure Advance
            </CardTitle>
            <CardDescription className="text-green-700 dark:text-green-300">
              Use this value in your printer settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-mono font-bold text-green-800 dark:text-green-200 text-center">
              {getDisplayResult()}
            </div>
          </CardContent>
        </Card>

        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Calculation Details</h4>
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex justify-between">
              <span>Pressure Advance Start:</span>
              <span className="font-mono">{pressureAdvanceStart}</span>
            </div>
            <div className="flex justify-between">
              <span>Pressure Advance Step:</span>
              <span className="font-mono">{pressureAdvanceStep}</span>
            </div>
            <div className="flex justify-between">
              <span>Measured Height (mm):</span>
              <span className="font-mono">{measuredHeight}</span>
            </div>
            <hr className="my-2 border-gray-300 dark:border-gray-600" />
            <div className="flex justify-between font-semibold">
              <span>Formula:</span>
              <span className="font-mono text-xs">Start + (Step × Height)</span>
            </div>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

export default PressureAdvanceResultsView;
